import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { usersService } from '../services/api';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const UsersTable = ({ role = 'all', title = 'Kullanıcılar' }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    sortBy: 'created_at',
    sortOrder: 'DESC'
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [showOrgModal, setShowOrgModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [userOrganizations, setUserOrganizations] = useState([]);
  const [editForm, setEditForm] = useState({ name: '', email: '', phone: '' });
  const [assignForm, setAssignForm] = useState({ organizationId: '', roleId: '' });
  const [passwordForm, setPasswordForm] = useState({ newPassword: '', confirmPassword: '' });

  useEffect(() => {
    loadUsers();
  }, [role, pagination.page, pagination.limit, filters]);

  useEffect(() => {
    if (showAssignModal) {
      loadOrganizations();
    }
  }, [showAssignModal]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      };

      const response = role === 'all' 
        ? await usersService.getAllUsers(params)
        : await usersService.getUsersByRole(role, params);

      if (response.success) {
        setUsers(response.data || []);
        setPagination(prev => ({
          ...prev,
          total: response.pagination?.total || 0,
          totalPages: response.pagination?.totalPages || 0
        }));
      } else {
        toast.error(response.error || 'Kullanıcılar yüklenemedi');
      }
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Kullanıcılar yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const loadOrganizations = async () => {
    const response = await usersService.getAllOrganizations();
    if (response.success) {
      setOrganizations(response.data || []);
    }
  };

  const loadUserOrganizations = async (userId) => {
    const response = await usersService.getUserOrganizations(userId);
    if (response.success) {
      setUserOrganizations(response.data || []);
      setShowOrgModal(true);
    }
  };

  const handleSearch = (e) => {
    setFilters(prev => ({ ...prev, search: e.target.value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleStatusFilter = (e) => {
    setFilters(prev => ({ ...prev, status: e.target.value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageSizeChange = (e) => {
    setPagination(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleToggleStatus = async (user) => {
    const response = await usersService.toggleUserStatus(user.id);
    if (response.success) {
      toast.success(response.message || 'Durum güncellendi');
      loadUsers();
    } else {
      toast.error(response.error || 'Durum güncellenemedi');
    }
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setEditForm({
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || ''
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedUser) return;

    const response = await usersService.updateUser(selectedUser.id, editForm);
    if (response.success) {
      toast.success('Kullanıcı güncellendi');
      setShowEditModal(false);
      loadUsers();
    } else {
      toast.error(response.error || 'Kullanıcı güncellenemedi');
    }
  };

  const handleAssign = (user) => {
    setSelectedUser(user);
    setAssignForm({ organizationId: '', roleId: '' });
    setShowAssignModal(true);
  };

  const handleSaveAssign = async () => {
    if (!selectedUser || !assignForm.organizationId || !assignForm.roleId) {
      toast.error('Lütfen organizasyon ve rol seçin');
      return;
    }

    const response = await usersService.assignUserToOrganization(
      selectedUser.id,
      assignForm.organizationId,
      parseInt(assignForm.roleId)
    );

    if (response.success) {
      toast.success('Kullanıcı organizasyona atandı');
      setShowAssignModal(false);
      loadUsers();
    } else {
      toast.error(response.error || 'Atama başarısız');
    }
  };

  const handleResetPassword = (user) => {
    setSelectedUser(user);
    setPasswordForm({ newPassword: '', confirmPassword: '' });
    setShowPasswordModal(true);
  };

  const handleSavePassword = async () => {
    if (!selectedUser) return;

    if (!passwordForm.newPassword || passwordForm.newPassword.length < 6) {
      toast.error('Şifre en az 6 karakter olmalıdır');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('Şifreler eşleşmiyor');
      return;
    }

    const response = await usersService.resetUserPassword(selectedUser.id, passwordForm.newPassword);
    if (response.success) {
      toast.success('Şifre sıfırlandı');
      setShowPasswordModal(false);
      setPasswordForm({ newPassword: '', confirmPassword: '' });
    } else {
      toast.error(response.error || 'Şifre sıfırlanamadı');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  return (
    <>
      <div className="card h-100 p-0 radius-12">
        <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
          <div className="d-flex align-items-center flex-wrap gap-3">
            <span className="text-md fw-medium text-secondary-light mb-0">Göster</span>
            <select
              className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px"
              value={pagination.limit}
              onChange={handlePageSizeChange}
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
            <form className="navbar-search">
              <input
                type="text"
                className="bg-base h-40-px w-auto"
                name="search"
                placeholder="Ara (Ad, Email, Telefon)..."
                value={filters.search}
                onChange={handleSearch}
              />
              <Icon icon="ion:search-outline" className="icon" />
            </form>
            <select
              className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px"
              value={filters.status}
              onChange={handleStatusFilter}
            >
              <option value="">Tüm Durumlar</option>
              <option value="active">Aktif</option>
              <option value="inactive">Pasif</option>
            </select>
          </div>
        </div>
        <div className="card-body p-24">
          {loading ? (
            <div className="text-center py-5">
              <p className="text-secondary-light">Yükleniyor...</p>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-5">
              <p className="text-secondary-light">Kullanıcı bulunamadı</p>
            </div>
          ) : (
            <>
              <div className="table-responsive scroll-sm">
                <table className="table bordered-table sm-table mb-0">
                  <thead>
                    <tr>
                      <th scope="col">S.L</th>
                      <th scope="col">Kayıt Tarihi</th>
                      <th scope="col">Ad Soyad</th>
                      <th scope="col">Email</th>
                      <th scope="col">Telefon</th>
                      <th scope="col">Organizasyon Sayısı</th>
                      <th scope="col" className="text-center">Durum</th>
                      <th scope="col" className="text-center">İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user, index) => (
                      <tr key={user.id}>
                        <td>{(pagination.page - 1) * pagination.limit + index + 1}</td>
                        <td>{formatDate(user.created_at)}</td>
                        <td>
                          <div className="d-flex align-items-center">
                            <div className="flex-grow-1">
                              <span className="text-md mb-0 fw-medium">
                                {user.name || '-'}
                              </span>
                              {user.is_owner && (
                                <span className="badge bg-primary ms-2">Owner</span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="text-md mb-0 fw-normal text-secondary-light">
                            {user.email || '-'}
                          </span>
                        </td>
                        <td>
                          <span className="text-md mb-0 fw-normal text-secondary-light">
                            {user.phone || '-'}
                          </span>
                        </td>
                        <td>
                          <span
                            className="text-primary-600 fw-medium cursor-pointer"
                            onClick={() => loadUserOrganizations(user.id)}
                            title="Organizasyonları görüntüle"
                          >
                            {user.organization_count || 0}
                          </span>
                        </td>
                        <td className="text-center">
                          <span
                            className={`${
                              user.active
                                ? 'bg-success-focus text-success-600 border-success-main'
                                : 'bg-danger-focus text-danger-600 border-danger-main'
                            } border px-24 py-4 radius-4 fw-medium text-sm`}
                          >
                            {user.active ? 'Aktif' : 'Pasif'}
                          </span>
                        </td>
                        <td className="text-center">
                          <div className="d-flex align-items-center gap-10 justify-content-center">
                            <button
                              type="button"
                              className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                              onClick={() => handleEdit(user)}
                              title="Düzenle"
                            >
                              <Icon icon="lucide:edit" className="icon text-lg" />
                            </button>
                            <button
                              type="button"
                              className={`${
                                user.active
                                  ? 'bg-warning-focus text-warning-600 bg-hover-warning-200'
                                  : 'bg-success-focus text-success-600 bg-hover-success-200'
                              } fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle`}
                              onClick={() => handleToggleStatus(user)}
                              title={user.active ? 'Pasif Yap' : 'Aktif Yap'}
                            >
                              <Icon
                                icon={user.active ? 'solar:eye-closed-outline' : 'solar:eye-outline'}
                                className="icon text-lg"
                              />
                            </button>
                            <button
                              type="button"
                              className="bg-primary-focus text-primary-600 bg-hover-primary-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                              onClick={() => handleAssign(user)}
                              title="Yetkilendir"
                            >
                              <Icon icon="solar:user-plus-outline" className="icon text-lg" />
                            </button>
                            <button
                              type="button"
                              className="bg-info-focus text-info-600 bg-hover-info-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                              onClick={() => handleResetPassword(user)}
                              title="Şifre Sıfırla"
                            >
                              <Icon icon="solar:key-outline" className="icon text-lg" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="d-flex align-items-center justify-content-between mt-24">
                  <div className="text-secondary-light">
                    Toplam {pagination.total} kayıt, {pagination.page}/{pagination.totalPages} sayfa
                  </div>
                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      disabled={pagination.page === 1}
                      onClick={() => handlePageChange(pagination.page - 1)}
                    >
                      Önceki
                    </button>
                    {[...Array(Math.min(5, pagination.totalPages))].map((_, i) => {
                      const pageNum = pagination.page <= 3 
                        ? i + 1 
                        : pagination.page >= pagination.totalPages - 2
                        ? pagination.totalPages - 4 + i
                        : pagination.page - 2 + i;
                      if (pageNum < 1 || pageNum > pagination.totalPages) return null;
                      return (
                        <button
                          key={pageNum}
                          className={`btn btn-sm ${pagination.page === pageNum ? 'btn-primary' : 'btn-outline-secondary'}`}
                          onClick={() => handlePageChange(pageNum)}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      disabled={pagination.page === pagination.totalPages}
                      onClick={() => handlePageChange(pagination.page + 1)}
                    >
                      Sonraki
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Organizations Modal */}
      {showOrgModal && selectedUser && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {selectedUser.name} - Organizasyonlar
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowOrgModal(false);
                    setUserOrganizations([]);
                  }}
                />
              </div>
              <div className="modal-body">
                {userOrganizations.length === 0 ? (
                  <p className="text-secondary-light">Organizasyon bulunamadı</p>
                ) : (
                  <ul className="list-group">
                    {userOrganizations.map((org) => (
                      <li key={org.id} className="list-group-item d-flex justify-content-between align-items-center">
                        <div>
                          <strong>{org.name}</strong>
                          {org.title && <div className="text-sm text-secondary-light">{org.title}</div>}
                        </div>
                        <span className={`badge ${org.relationship_type === 'owner' ? 'bg-primary' : 'bg-secondary'}`}>
                          {org.relationship_type === 'owner' ? 'Owner' : org.role_name || 'Atanmış'}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowOrgModal(false);
                    setUserOrganizations([]);
                  }}
                >
                  Kapat
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedUser && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Kullanıcı Düzenle</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowEditModal(false)}
                />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Ad Soyad</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editForm.name}
                    onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-control"
                    value={editForm.email}
                    onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Telefon</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editForm.phone}
                    onChange={(e) => setEditForm(prev => ({ ...prev, phone: e.target.value }))}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowEditModal(false)}
                >
                  İptal
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveEdit}
                >
                  Kaydet
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assign Modal */}
      {showAssignModal && selectedUser && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Kullanıcıyı Organizasyona Ata</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowAssignModal(false)}
                />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Organizasyon</label>
                  <select
                    className="form-select"
                    value={assignForm.organizationId}
                    onChange={(e) => setAssignForm(prev => ({ ...prev, organizationId: e.target.value }))}
                  >
                    <option value="">Seçiniz...</option>
                    {organizations.map((org) => (
                      <option key={org.id} value={org.id}>
                        {org.name} {org.title && `(${org.title})`}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">Rol</label>
                  <select
                    className="form-select"
                    value={assignForm.roleId}
                    onChange={(e) => setAssignForm(prev => ({ ...prev, roleId: e.target.value }))}
                  >
                    <option value="">Seçiniz...</option>
                    <option value="3">Personel</option>
                    <option value="4">Müşteri</option>
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowAssignModal(false)}
                >
                  İptal
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveAssign}
                >
                  Ata
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Password Reset Modal */}
      {showPasswordModal && selectedUser && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Şifre Sıfırla - {selectedUser.name}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPasswordForm({ newPassword: '', confirmPassword: '' });
                  }}
                />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Yeni Şifre</label>
                  <input
                    type="password"
                    className="form-control"
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                    minLength={6}
                  />
                  <small className="text-secondary-light">En az 6 karakter</small>
                </div>
                <div className="mb-3">
                  <label className="form-label">Şifre Tekrar</label>
                  <input
                    type="password"
                    className="form-control"
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    minLength={6}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPasswordForm({ newPassword: '', confirmPassword: '' });
                  }}
                >
                  İptal
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSavePassword}
                >
                  Şifreyi Sıfırla
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UsersTable;

